import type { ISearchTerm, ISearchTermsGrowth, SearchTermKind } from "../../types";
import { rotuloDoMes } from "./search-terms";

// Resumo das buscas do Google para o card do dashboard e a página "Ver mais":
// o que o dono entende em 5 segundos — quanto buscaram e por onde chegaram.

export type FiltroDeTipo = "TODOS" | SearchTermKind;

export interface ResumoDasBuscas {
  totalRecente: number;
  crescimento: number | null;
  porServico: number;
  porNome: number;
  porEndereco: number;
  percentualPorServico: number | null;
  percentualPorNome: number | null;
  periodoRecente: string;
}

const JANELA = 3;

const soma = (valores: (number | null | undefined)[]): number =>
  valores.reduce<number>((total, valor) => total + (valor ?? 0), 0);

const ultimos = <T>(lista: T[], quantos: number): T[] =>
  lista.slice(Math.max(0, lista.length - quantos));

/** Últimos 3 meses: total, crescimento sobre os 3 anteriores e origem das buscas. */
export const resumoDasBuscas = (dados: ISearchTermsGrowth): ResumoDasBuscas => {
  const n = dados.totalByMonth.length;
  const totalRecente = soma(ultimos(dados.totalByMonth, JANELA));
  const totalAnterior = n >= 2 * JANELA ? soma(dados.totalByMonth.slice(n - 2 * JANELA, n - JANELA)) : 0;
  const crescimento =
    n >= 2 * JANELA && totalAnterior > 0
      ? Math.round(((totalRecente - totalAnterior) / totalAnterior) * 100)
      : null;

  const porTipo = (tipo: SearchTermKind) =>
    soma(dados.groups.filter((grupo) => grupo.kind === tipo).map((grupo) => soma(ultimos(grupo.values, JANELA))));
  const porServico = porTipo("TOPIC");
  const porNome = porTipo("BRAND");
  const porEndereco = porTipo("ADDRESS");
  const base = porServico + porNome + porEndereco;
  const meses = ultimos(dados.months, JANELA);

  return {
    totalRecente,
    crescimento,
    porServico,
    porNome,
    porEndereco,
    percentualPorServico: base > 0 ? Math.round((porServico / base) * 100) : null,
    percentualPorNome: base > 0 ? Math.round((porNome / base) * 100) : null,
    periodoRecente: meses.length ? `${rotuloDoMes(meses[0])} a ${rotuloDoMes(meses[meses.length - 1])}` : "",
  };
};

const normalizar = (texto: string) =>
  texto.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();

export const filtrarTermos = (
  termos: ISearchTerm[],
  { busca, tipo }: { busca: string; tipo: FiltroDeTipo },
): ISearchTerm[] => {
  const alvo = normalizar(busca);
  return termos.filter(
    (termo) => (tipo === "TODOS" || termo.kind === tipo) && (!alvo || normalizar(termo.term).includes(alvo)),
  );
};

export const contarPorTipo = (termos: ISearchTerm[]): Record<FiltroDeTipo, number> => ({
  TODOS: termos.length,
  TOPIC: termos.filter((termo) => termo.kind === "TOPIC").length,
  BRAND: termos.filter((termo) => termo.kind === "BRAND").length,
  ADDRESS: termos.filter((termo) => termo.kind === "ADDRESS").length,
});

/** Altura (0–100) de cada um dos últimos meses em relação ao maior deles. */
export const barrasRecentes = (valores: (number | null)[], quantos = 6): number[] => {
  const recentes = ultimos(valores, quantos).map((valor) => valor ?? 0);
  const maior = Math.max(0, ...recentes);
  return recentes.map((valor) => (maior > 0 ? Math.round((valor / maior) * 100) : 0));
};
